import { useEffect, useRef, useState } from "react";
import { Disc3, Pause, Play } from "lucide-react";

const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

export const AlbumPlayer = ({ playerId, title, year, tracks }) => {
  const audioRef = useRef(null);
  const [current, setCurrent] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (current === null || !audio) return;
    audio.src = tracks[current].src;
    audio.play().catch(() => setPlaying(false));
    window.dispatchEvent(new CustomEvent("cb-audio-play", { detail: playerId }));
  }, [current, tracks, playerId]);

  useEffect(() => {
    const pauseIfOther = (e) => {
      if (e.detail !== playerId && audioRef.current) {
        audioRef.current.pause();
        setPlaying(false);
      }
    };
    window.addEventListener("cb-audio-play", pauseIfOther);
    return () => window.removeEventListener("cb-audio-play", pauseIfOther);
  }, [playerId]);

  const toggle = (i) => {
    const audio = audioRef.current;
    if (current === i) {
      if (playing) {
        audio.pause();
        setPlaying(false);
      } else {
        audio.play().catch(() => {});
        setPlaying(true);
        window.dispatchEvent(new CustomEvent("cb-audio-play", { detail: playerId }));
      }
    } else {
      setCurrent(i);
      setPlaying(true);
    }
  };

  const seek = (e) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    audio.currentTime = ((e.clientX - rect.left) / rect.width) * duration;
  };

  const onEnded = () => {
    if (current !== null && current < tracks.length - 1) {
      setCurrent(current + 1);
    } else {
      setPlaying(false);
      setTime(0);
    }
  };

  return (
    <div data-testid={`album-player-${playerId}`} className="relative mt-10 border border-gold/20 bg-card p-6 md:p-8">
      <div className="flex items-center gap-4">
        <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-gold/40 text-gold">
          <Disc3 size={22} className={playing ? "animate-[spin_3s_linear_infinite]" : ""} />
        </span>
        <div>
          <p className="font-serif text-xl md:text-2xl text-cream">{title} · {year}</p>
          <p className="mt-1 text-[11px] uppercase tracking-[0.25em] text-mute">Álbum completo · {tracks.length} canciones</p>
        </div>
      </div>

      <div className="mt-6" data-testid="album-tracklist">
        {tracks.map((t, i) => {
          const active = current === i;
          return (
            <div key={t.title} data-testid={`track-row-${i + 1}`} className={`border-t border-cream/10 ${active ? "bg-cream/[0.03]" : ""}`}>
              <button
                data-testid={`track-play-${i + 1}`}
                onClick={() => toggle(i)}
                className="group flex w-full items-center gap-4 py-3.5 px-2 text-left transition-colors hover:bg-cream/[0.02]"
              >
                <span className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition-all duration-300 ${active ? "border-gold bg-gold text-ink" : "border-cream/25 text-sand group-hover:border-gold group-hover:text-gold"}`}>
                  {active && playing ? <Pause size={15} fill="currentColor" /> : <Play size={15} className="translate-x-px" fill="currentColor" />}
                </span>
                <span className="text-xs text-mute w-6">{String(i + 1).padStart(2, "0")}</span>
                <span className={`flex-1 font-serif text-lg md:text-xl transition-colors ${active ? "text-gold" : "text-cream group-hover:text-gold"}`}>{t.title}</span>
                {active && duration > 0 && (
                  <span className="text-xs text-mute tabular-nums">
                    {fmt(time)} / {fmt(duration)}
                  </span>
                )}
              </button>
              {active && (
                <div className="px-2 pb-4">
                  <div
                    data-testid="track-progress"
                    onClick={seek}
                    className="h-1 w-full cursor-pointer bg-cream/10"
                    role="progressbar"
                    aria-valuenow={Math.round((time / (duration || 1)) * 100)}
                  >
                    <div className="h-full bg-gold transition-[width] duration-200" style={{ width: `${(time / (duration || 1)) * 100}%` }} />
                  </div>
                </div>
              )}
            </div>
          );
        })}
        <div className="border-t border-cream/10" />
      </div>

      <audio ref={audioRef} preload="metadata" onTimeUpdate={(e) => { setTime(e.target.currentTime); setDuration(e.target.duration || 0); }} onEnded={onEnded} data-testid="album-audio" />
    </div>
  );
};
